import React, { useState } from 'react';
import { Controller, Control } from 'react-hook-form';
import Select, {
  SingleValue,
  InputActionMeta,
  StylesConfig,
} from 'react-select';
import type { BankAccount } from '@/types';
import type { BankOption } from './BankAccountForm';

const bankSelectStyles: StylesConfig<BankOption, false> = {
  option: (provided) => ({
    ...provided,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    ':hover': {
      ...provided[':hover'],
      backgroundColor: 'transparent',
      cursor: 'pointer',
    },
    ':active': {
      ...provided[':active'],
      backgroundColor: 'transparent',
      cursor: 'pointer',
    },
  }),
};

type Props = {
  control: Control<BankAccount>;
  bankOptions: BankOption[];
  initialBankName?: string;
  onBankChange: (
    option: BankOption | null,
    fieldOnChange: (value: string) => void
  ) => void;
};

const BankSelect: React.FC<Props> = ({
  control,
  bankOptions,
  initialBankName,
  onBankChange,
}) => {
  const [searchInput, setSearchInput] = useState('');

  const handleBankInputChange = (value: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') {
      setSearchInput(value);
    }
    if (actionMeta.action === 'menu-close') {
      setSearchInput('');
    }
    return value;
  };

  return (
    <Controller
      name='bankName'
      control={control}
      rules={{ required: '金融機関名は必須です' }}
      defaultValue={initialBankName ?? ''}
      render={({ field }) => {
        const selectedOption =
          bankOptions.find((opt) => opt.bank.name === (field.value ?? '')) ??
          null;

        return (
          <Select<BankOption, false>
            inputId='searchBank'
            placeholder='銀行名を検索する'
            classNamePrefix='bank-select'
            options={bankOptions}
            value={selectedOption}
            inputValue={searchInput}
            onInputChange={handleBankInputChange}
            onChange={(option: SingleValue<BankOption>) =>
              onBankChange(option ?? null, field.onChange)
            }
            onBlur={field.onBlur}
            isClearable
            openMenuOnClick={false}
            openMenuOnFocus={false}
            menuIsOpen={searchInput.trim().length > 0}
            styles={bankSelectStyles}
            filterOption={(option, rawInput) => {
              const keyword = rawInput.replace(/銀行/g, '').trim();
              if (!keyword) return false;
              const { bank } = option.data;
              return (
                bank.name.includes(keyword) ||
                bank.hira.includes(keyword) ||
                bank.kana.includes(keyword)
              );
            }}
            noOptionsMessage={() =>
              searchInput.trim().length === 0
                ? '文字を入力してください'
                : '該当の銀行は見つかりません'
            }
          />
        );
      }}
    />
  );
};

export default BankSelect;
